/**
 * 通用列表导出 API（list-export-operations）。
 *
 * - POST /exports -> 创建任务（≤10,000 同步完成即含下载链接；超限返回排队句柄）
 * - GET /exports/:id -> 任务状态（轮询至终态）
 * - GET /exports/:id/download -> 文件下载（Blob，短时有效，逐次审计）
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 一期支持的导出目标列表。 */
export type ExportTarget = 'operation-logs' | 'students' | 'orders' | 'revenue-sku';

/** 创建导出任务响应（统一任务模型）。 */
export interface CreateExportResult {
  taskId: string;
  status: 'queued' | 'completed';
  rowCount?: number;
  downloadUrl?: string;
}

/** 导出任务状态。 */
export interface ExportTaskStatus {
  taskId: string;
  target: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  rowCount: number | null;
  failReason: string | null;
  expiresAt: string | null;
  createdAt: string;
  downloadUrl: string | null;
}

/** 经临时 `<a download>` 触发浏览器保存文件，完成后立即释放 blob URL。 */
function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function createExport(
  target: ExportTarget,
  filters: Record<string, unknown> = {},
): Promise<CreateExportResult> {
  return http.post<CreateExportResult>(ApiPaths.exports.create, { target, filters });
}

function getExportStatus(taskId: string): Promise<ExportTaskStatus> {
  return http.get<ExportTaskStatus>(ApiPaths.exports.status(taskId));
}

/** 下载导出文件并触发浏览器保存（文件名取服务端 Content-Disposition，缺省回退）。 */
async function downloadExportFile(taskId: string): Promise<void> {
  const { blob, filename } = await http.download(ApiPaths.exports.download(taskId));
  triggerBrowserDownload(blob, filename ?? `export-${taskId.slice(0, 8)}.csv`);
}

/** 导出 API 唯一对外入口。 */
export const exportsApi = {
  createExport,
  getExportStatus,
  downloadExportFile,
};
